import {
    collection,
    doc,
    getDocs,
    query,
    orderBy,
    limit,
    Timestamp,
    runTransaction,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Transaction, OperatorAccount, FinanceStats } from '../types';

export class FinanceService {
    private static readonly TRANSACTIONS_COL = 'transactions';
    private static readonly OPERATORS_COL = 'operatorAccounts';

    static async getFinanceStats(): Promise<FinanceStats> {
        try {
            const transactionsRef = collection(db, this.TRANSACTIONS_COL);
            const snapshot = await getDocs(transactionsRef);

            let totalRevenue = 0;
            let totalPayouts = 0;
            let pendingCollections = 0;

            snapshot.docs.forEach(doc => {
                const data = doc.data();
                const amount = data.amount || 0;
                if (data.type === 'collection') {
                    if (data.status === 'completed') totalRevenue += amount;
                    else if (data.status === 'pending') pendingCollections += amount;
                } else if (data.type === 'payout' && data.status === 'completed') {
                    totalPayouts += amount;
                }
            });

            const recentTransactions = await this.getRecentTransactions(10);

            return {
                totalRevenue,
                pendingCollections,
                totalPayouts,
                recentTransactions,
            };
        } catch (error: any) {
            throw new Error(`Failed to fetch finance stats: ${error.message}`);
        }
    }

    static async getRecentTransactions(limitCount: number = 20): Promise<Transaction[]> {
        try {
            const transactionsRef = collection(db, this.TRANSACTIONS_COL);
            const q = query(transactionsRef, orderBy('createdAt', 'desc'), limit(limitCount));
            const snapshot = await getDocs(q);

            return snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    createdAt: data.createdAt?.toDate?.() || new Date(),
                } as Transaction;
            });
        } catch (error: any) {
            console.error('Error fetching transactions:', error);
            return [];
        }
    }

    static async getOperatorAccounts(): Promise<OperatorAccount[]> {
        try {
            const accountsRef = collection(db, this.OPERATORS_COL);
            const q = query(accountsRef, orderBy('totalRevenue', 'desc'));
            const snapshot = await getDocs(q);

            return snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    lastPaymentAt: data.lastPaymentAt?.toDate?.() || undefined,
                } as OperatorAccount;
            });
        } catch (error: any) {
            console.error('Error fetching operator accounts:', error);
            return [];
        }
    }

    /**
     * Processes a payout for an operator.
     * Atomically updates operator balance and creates a transaction record.
     */
    static async createPayout(operatorId: string, amount: number, description: string): Promise<void> {
        try {
            const operatorRef = doc(db, this.OPERATORS_COL, operatorId);
            const transactionRef = collection(db, this.TRANSACTIONS_COL);

            await runTransaction(db, async (transaction) => {
                const operatorDoc = await transaction.get(operatorRef);
                if (!operatorDoc.exists()) {
                    throw new Error("Operator does not exist!");
                }

                const currentBalance = operatorDoc.data().balance || 0;
                if (currentBalance < amount) {
                    throw new Error("Insufficient balance for payout.");
                }

                const newBalance = currentBalance - amount;
                const totalPayouts = (operatorDoc.data().totalPayouts || 0) + amount;

                // 1. Update operator balance and last payment info
                transaction.update(operatorRef, {
                    balance: newBalance,
                    totalPayouts: totalPayouts,
                    lastPaymentAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                });

                // 2. Create the payout transaction record
                const newTxRef = doc(transactionRef);
                transaction.set(newTxRef, {
                    amount,
                    type: 'payout',
                    status: 'completed',
                    operatorId,
                    description,
                    createdAt: Timestamp.now(),
                });
            });
        } catch (error: any) {
            throw new Error(`Payout failed: ${error.message}`);
        }
    }
}
